import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { validateGoogleToken } from "../utils/googleCalendar";
import { googleProxy } from "../services/api/secureProxies";
import { useAuth } from "./AuthContext";

const AGENDA_EMPLOYEE_KEY = "google_agenda_employee_id";

interface GoogleAuthContextType {
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** null = agenda padrão da loja */
  agendaEmployeeId: string | null;
  setAgendaEmployeeId: (employeeId: string | null) => void;
  setAccessToken: (token: string) => Promise<void>;
  clearAccessToken: () => Promise<void>;
  validateToken: () => Promise<boolean>;
  autoValidateToken: () => Promise<boolean>;
  logout: () => Promise<void>;
  reloadTokens: () => Promise<void>;
}

const GoogleAuthContext = createContext<GoogleAuthContextType | undefined>(
  undefined
);

export const useGoogleAuth = () => {
  const context = useContext(GoogleAuthContext);
  if (context === undefined) {
    throw new Error("useGoogleAuth must be used within a GoogleAuthProvider");
  }
  return context;
};

interface GoogleAuthProviderProps {
  children: ReactNode;
}

async function restoreFromBackend(
  cnpj: string,
  employeeId?: string | null
): Promise<string | null> {
  try {
    const st = await googleProxy.status(cnpj, employeeId || undefined);
    const fromStatus = st?.access_token ? String(st.access_token) : "";
    if (fromStatus) return fromStatus;
    if (st?.connected || st?.has_refresh) {
      const refreshed = await googleProxy.refresh(cnpj, employeeId || undefined);
      if (refreshed?.access_token) return String(refreshed.access_token);
    }
  } catch {
    try {
      const refreshed = await googleProxy.refresh(cnpj, employeeId || undefined);
      if (refreshed?.access_token) return String(refreshed.access_token);
    } catch {
      /* sem tokens no backend */
    }
  }
  return null;
}

export const GoogleAuthProvider: React.FC<GoogleAuthProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agendaEmployeeId, setAgendaEmployeeIdState] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem(AGENDA_EMPLOYEE_KEY) || null;
      } catch {
        return null;
      }
    }
  );

  const setAgendaEmployeeId = useCallback((employeeId: string | null) => {
    setAgendaEmployeeIdState(employeeId);
    try {
      if (employeeId) localStorage.setItem(AGENDA_EMPLOYEE_KEY, employeeId);
      else localStorage.removeItem(AGENDA_EMPLOYEE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (user?.isCollaborator && user.employeeId) {
      setAgendaEmployeeId(user.employeeId);
    }
  }, [user?.isCollaborator, user?.employeeId, setAgendaEmployeeId]);

  const scopedEmployeeId = user?.isCollaborator
    ? user.employeeId || agendaEmployeeId
    : agendaEmployeeId;

  useEffect(() => {
    let cancelled = false;
    const loadSavedToken = async () => {
      try {
        const cnpj = user?.cnpj?.replace(/\D/g, "") || "";
        if (cnpj.length !== 14) {
          if (!cancelled) setIsLoading(false);
          return;
        }

        if (user?.isCollaborator && !scopedEmployeeId) {
          if (!cancelled) {
            setAccessTokenState(null);
            setIsAuthenticated(false);
            setIsLoading(false);
          }
          return;
        }

        const fromBackend = await restoreFromBackend(cnpj, scopedEmployeeId);
        if (cancelled) return;
        if (fromBackend) {
          localStorage.setItem("google_access_token", fromBackend);
          setAccessTokenState(fromBackend);
          setIsAuthenticated(true);
          setError(null);
          return;
        }

        if (scopedEmployeeId || user?.isCollaborator) {
          localStorage.removeItem("google_access_token");
          if (!cancelled) {
            setAccessTokenState(null);
            setIsAuthenticated(false);
            setError(null);
          }
          return;
        }

        const localToken = localStorage.getItem("google_access_token");
        if (localToken && (await validateGoogleToken(localToken))) {
          if (cancelled) return;
          setAccessTokenState(localToken);
          setIsAuthenticated(true);
          setError(null);
          return;
        }

        if (!cancelled) {
          setAccessTokenState(null);
          setIsAuthenticated(false);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError("Erro ao carregar autenticação do Google");
          setAccessTokenState(null);
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    setIsLoading(true);
    void loadSavedToken();
    return () => {
      cancelled = true;
    };
  }, [user?.cnpj, user?.isCollaborator, scopedEmployeeId]);

  const setAccessToken = async (token: string) => {
    setAccessTokenState(token);
    setIsAuthenticated(true);
    setError(null);
    localStorage.setItem("google_access_token", token);
  };

  const clearAccessToken = async () => {
    setAccessTokenState(null);
    setIsAuthenticated(false);
    setError(null);
    localStorage.removeItem("google_access_token");
    localStorage.removeItem("google_refresh_token");
  };

  const validateToken = async (): Promise<boolean> => {
    const cnpj = user?.cnpj?.replace(/\D/g, "") || "";

    if (accessToken) {
      try {
        if (await validateGoogleToken(accessToken)) return true;
      } catch {
        /* tenta renovar */
      }
    }

    if (cnpj.length === 14) {
      const fromBackend = await restoreFromBackend(cnpj, scopedEmployeeId);
      if (fromBackend) {
        setAccessTokenState(fromBackend);
        localStorage.setItem("google_access_token", fromBackend);
        setIsAuthenticated(true);
        setError(null);
        return true;
      }
    }

    setAccessTokenState(null);
    setIsAuthenticated(false);
    localStorage.removeItem("google_access_token");
    setError("Token do Google expirou. É necessário reconectar.");
    return false;
  };

  const autoValidateToken = async (): Promise<boolean> => {
    if (!accessToken || !isAuthenticated) {
      if (user?.cnpj) {
        return await validateToken();
      }
      return false;
    }
    try {
      if (await validateGoogleToken(accessToken)) return true;
      return await validateToken();
    } catch {
      return false;
    }
  };

  const logout = async () => {
    await clearAccessToken();
  };

  const reloadTokens = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const cnpj = user?.cnpj?.replace(/\D/g, "") || "";
      if (cnpj.length !== 14) {
        setAccessTokenState(null);
        setIsAuthenticated(false);
        return;
      }
      const fromBackend = await restoreFromBackend(cnpj, scopedEmployeeId);
      if (fromBackend) {
        localStorage.setItem("google_access_token", fromBackend);
        setAccessTokenState(fromBackend);
        setIsAuthenticated(true);
        return;
      }
      setAccessTokenState(null);
      setIsAuthenticated(false);
    } catch {
      setError("Erro ao recarregar tokens do Google");
    } finally {
      setIsLoading(false);
    }
  };

  const value: GoogleAuthContextType = {
    accessToken,
    isAuthenticated,
    isLoading,
    error,
    agendaEmployeeId,
    setAgendaEmployeeId,
    setAccessToken,
    clearAccessToken,
    validateToken,
    autoValidateToken,
    logout,
    reloadTokens,
  };

  return (
    <GoogleAuthContext.Provider value={value}>
      {children}
    </GoogleAuthContext.Provider>
  );
};
